import React from "react";
import PropTypes from "prop-types";
import { C } from "../reporting/reportingStyles";

/**
 * Pill-style tabs, the same look as the Product Recommendations page.
 *
 * Two differences from the copy in recommendations.js, both because these sit inside forms:
 *   - every button is type="button", so switching tab never submits the form around it;
 *   - the buttons carry role="tab" / aria-selected, so tests and screen readers can find
 *     "the Discount tab" by name instead of by position.
 *
 * A tab may carry a `badge` (a count shown after the label) and an `alert` flag (a dot, for
 * "something on this tab needs attention").
 */
const PillTabs = ({ tabs, value, onChange, ariaLabel }) => (
    <div role="tablist" aria-label={ariaLabel} style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {tabs.map((t) => {
            const selected = t.key === value;
            return (
                <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => onChange(t.key)}
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                        border: selected ? `1.5px solid ${C.coral}` : `1px solid ${C.border}`,
                        background: selected ? C.coralLight : C.white,
                        color: selected ? C.coral : C.text,
                    }}
                >
                    {t.label}
                    {t.badge !== undefined && t.badge !== null && t.badge !== 0 && (
                        <span
                            style={{
                                minWidth: 18, padding: "0 6px", borderRadius: 999, fontSize: 11, lineHeight: "18px",
                                background: selected ? C.coral : C.borderLight, color: selected ? C.white : C.textMed,
                            }}
                        >
                            {t.badge}
                        </span>
                    )}
                    {t.alert && (
                        <span aria-label="needs attention" style={{ width: 7, height: 7, borderRadius: "50%", background: C.orange }} />
                    )}
                </button>
            );
        })}
    </div>
);

PillTabs.propTypes = {
    /** [{ key, label, badge?, alert? }] */
    tabs: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        alert: PropTypes.bool,
    })).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
};

export default PillTabs;
